/* eslint-disable unicorn/explicit-length-check */
import isEmpty from "lodash/isEmpty";
import { Post } from "../types";
import { PostMediaFile } from "../../../shared/types/post";
import { AccountType } from "../../../shared/types/accounts";
import {
  getNewPostObj,
  isSingleDocument,
  isSingleImage,
  isSingleVideo,
} from "../../../utils/posts";
import { pickThumbnailFallback } from "../../../utils/media";

export function generateUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments that do not support crypto.randomUUID
  // eslint-disable-next-line unicorn/prefer-string-replace-all
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    // eslint-disable-next-line unicorn/prefer-math-trunc
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
class CreatePostNormalizer {
  makePostForCreate({
    post,
    assets,
    accounts,
    caption,
  }: {
    post: Partial<Post>;
    assets: PostMediaFile[];
    accounts: AccountType[] | undefined;
    caption: string | undefined;
  }): Post {
    const newPost = getNewPostObj(post) as unknown as Post;
    const postObject = {
      ...newPost,
      // eslint-disable-next-line unicorn/no-negated-condition
      mediaFiles: !isEmpty(newPost.mediaFiles) ? newPost.mediaFiles : assets,
      ...formatAssetsToPost(assets),
      accountIds:
        accounts && accounts.length > 0 ? accounts.map(({ _id }) => _id) : [],
      postClusterId: generateUUID(),
    } as Post;

    if (caption) {
      postObject.caption = caption;
    }
    this.normalizePostType(postObject);
    return postObject;
  }
  private normalizePostType(postObject: Post) {
    if (postObject.isStory) {
      postObject.type = "story";
      return;
    }
    if (postObject.mediaFiles?.length === 1) {
      if (isSingleVideo(postObject.mediaFiles, postObject)) {
        postObject.type = "video";
        postObject.isVideo = true;
        return;
      }
      if (isSingleDocument(postObject.mediaFiles, postObject)) {
        postObject.type = "document";
        postObject.isVideo = false;
        return;
      }
      if (isSingleImage(postObject.mediaFiles, postObject)) {
        postObject.type = "image";
        postObject.isVideo = false;
        return;
      }
    }
    if (postObject.mediaFiles?.length > 0) {
      postObject.type = "multiMedia";
      postObject.isVideo = false;
    } else if (postObject.type === "multiMedia") {
      // this post should not be multiMedia anymore as no mediaFiles included…
      // should not have any text posts here as no way to convert from multiMedia to text, so assume either video or image
      const isVideo = postObject.url?.includes(".mp4") || false;
      postObject.type = isVideo ? "video" : "image";
      postObject.isVideo = isVideo;
    } else {
      if (
        postObject.type === "image" ||
        postObject.type === "video" ||
        // by default post comes with a text value, but when we create the post from the library
        // we should recheck post type according to the existing assets
        (postObject.type === "text" && !postObject.mediaFiles?.length)
      ) {
        // do nothing - this is fine - multiMedia is handled above
      } else {
        // draft has an invalid post type. Guess it from the file URL as this will be a single image/video
        if (postObject.type && !postObject.url) {
          if (
            postObject.type.includes("image") &&
            !postObject.type.includes("gif")
          ) {
            postObject.type = "image";
            postObject.isVideo = false;
          } else if (
            postObject.type.includes("document") ||
            postObject.type.includes("pdf")
          ) {
            postObject.type = "document";
            postObject.isVideo = false;
          } else {
            postObject.type = "video";
            postObject.isVideo = true;
          }
        } else {
          if (postObject.url) {
            if (
              postObject.url.includes(".jpg") ||
              postObject.url.includes(".jpeg") ||
              postObject.url.includes(".png")
            ) {
              postObject.type = "image";
              postObject.isVideo = false;
            } else if (
              postObject.url.includes(".mp4") ||
              postObject.url.includes(".gif")
            ) {
              postObject.type = "video";
              postObject.isVideo = true;
            } else if (postObject.url.includes(".pdf")) {
              postObject.type = "document";
              postObject.isVideo = false;
            }
          } else {
            postObject.type = "text";
            postObject.isVideo = false;
          }
        }
      }
    }
  }
}

// if assets exists, make sure we set the keys on the parent object
// correctly so that it will show the appropriate type and files
const formatAssetsToPost = (assets: PostMediaFile[]): Partial<Post> => {
  if (assets && assets.length) {
    const first = assets[0];
    // SKE-3646: never plant a video URL (`.mp4` etc.) into the image
    // thumbnail slot — the cover preview <img> renders broken when this
    // happens. Use pickThumbnailFallback so we either get a valid image
    // URL (thumbnail / coverFrame / image URL) or leave the slot empty
    // so the UI can render a placeholder/spinner.
    const thumbnailUrl = pickThumbnailFallback(first);
    // Not 100% sure if the url fields are required? Type definitely is
    return {
      url: first.url,
      originalUrl: first.originalUrl || first.origUrl || first.url,
      origUrl: first.origUrl || first.originalUrl || first.url,
      thumbnailUrl: thumbnailUrl ?? "",
      fileName: first.fileName || "",
      isVideo: first?.isVideo || false,
      meta: first.meta,
    };
  }
  return {};
};

export const createPostNormalizer = new CreatePostNormalizer();
